import { Route, Routes } from 'react-router-dom';
import { AppLayout } from './components/Layout';
import { RequireAuth, RequireRole } from './lib/auth';
import { GatewayRole } from './lib/enums';
import { OnboardingProvider, RequireVerifiedTenant } from './lib/onboarding';
import { AcceptInvitationPage } from './pages/AcceptInvitation';
import { ActivatePage } from './pages/Activate';
import { AiLearningPage } from './pages/AiLearning';
import { AiSettingsPage } from './pages/AiSettings';
import { ApprovalsPage } from './pages/Approvals';
import { AssistantPage } from './pages/Assistant';
import { PlatformPage } from './pages/Platform';
import { CheckEmailPage } from './pages/CheckEmail';
import { CompanyDirectoryPage } from './pages/CompanyDirectory';
import { DashboardPage } from './pages/Dashboard';
import { DocumentsPage } from './pages/Documents';
import { EmailIntakePage } from './pages/EmailIntake';
import { ExpensesPaymentsPage } from './pages/ExpensesPayments';
import { ForgotPasswordPage } from './pages/ForgotPassword';
import { NotFoundPage } from './pages/NotFound';
import { PartnerConnectionsPage } from './pages/PartnerConnections';
import { PartnerDeliveriesPage } from './pages/PartnerDeliveries';
import { PartnerOutboundPage } from './pages/PartnerOutbound';
import { PayablesAgentPage } from './pages/PayablesAgent';
import { PurchaseInvoicesPage } from './pages/PurchaseInvoices';
import { PurchaseOrdersPage } from './pages/PurchaseOrders';
import { ResetPasswordPage } from './pages/ResetPassword';
import { SettingsPage } from './pages/Settings';
import { SignInPage } from './pages/SignIn';
import { SignUpPage } from './pages/SignUp';
import { SyncHistoryPage } from './pages/SyncHistory';
import { SyncRulesPage } from './pages/SyncRules';
import { VendorIntakePage } from './pages/VendorIntake';
import { VendorInvoiceIntakePage } from './pages/VendorInvoiceIntake';
import { VendorsPage } from './pages/Vendors';
import AccountsPage from './pages/Accounts';

export function App(): JSX.Element {
  return (
    <Routes>
      {/* Public front door — no sidebar, no session required. */}
      <Route path="/signin" element={<SignInPage />} />
      <Route path="/signup" element={<SignUpPage />} />
      <Route path="/signup/check-email" element={<CheckEmailPage />} />
      <Route path="/activate" element={<ActivatePage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/invite" element={<AcceptInvitationPage />} />

      <Route
        element={
          <RequireAuth>
            <OnboardingProvider>
              <AppLayout />
            </OnboardingProvider>
          </RequireAuth>
        }
      >
        <Route path="/" element={<DashboardPage />} />

        {/* Every feature page sits behind the licence-verification gate, mirroring the API's own
            TenantVerificationFilter. The dashboard and settings stay reachable so an unverified
            tenant can see their state, resend the email and manage their password. */}
        <Route
          path="/documents"
          element={
            <RequireVerifiedTenant>
              <DocumentsPage />
            </RequireVerifiedTenant>
          }
        />
        <Route
          path="/purchase-invoices"
          element={
            <RequireVerifiedTenant>
              <PurchaseInvoicesPage />
            </RequireVerifiedTenant>
          }
        />
        <Route
          path="/approvals"
          element={
            <RequireVerifiedTenant>
              <ApprovalsPage />
            </RequireVerifiedTenant>
          }
        />
        <Route
          path="/vendors"
          element={
            <RequireVerifiedTenant>
              <VendorsPage />
            </RequireVerifiedTenant>
          }
        />
        <Route
          path="/vendor-intake"
          element={
            <RequireVerifiedTenant>
              <VendorIntakePage />
            </RequireVerifiedTenant>
          }
        />
        <Route
          path="/vendor-invoice-intake"
          element={
            <RequireVerifiedTenant>
              <VendorInvoiceIntakePage />
            </RequireVerifiedTenant>
          }
        />
        {/*
          Trading partners. Gated at Operator, not Auditor: everything on both screens is either
          drafting a disclosure or acting on an agreement with another company, and an auditor has
          nothing to do there. The admin-only actions - approve, publish, request, respond, revoke,
          bind - gate themselves inside the pages, and the server refuses them regardless.
        */}
        <Route
          path="/partners/directory"
          element={
            <RequireVerifiedTenant>
              <RequireRole minRole={GatewayRole.Operator}>
                <CompanyDirectoryPage />
              </RequireRole>
            </RequireVerifiedTenant>
          }
        />
        <Route
          path="/partners/connections"
          element={
            <RequireVerifiedTenant>
              <RequireRole minRole={GatewayRole.Operator}>
                <PartnerConnectionsPage />
              </RequireRole>
            </RequireVerifiedTenant>
          }
        />
        <Route
          path="/partners/deliveries"
          element={
            <RequireVerifiedTenant>
              <RequireRole minRole={GatewayRole.Operator}>
                <PartnerDeliveriesPage />
              </RequireRole>
            </RequireVerifiedTenant>
          }
        />
        <Route
          path="/partners/outbound"
          element={
            <RequireVerifiedTenant>
              <RequireRole minRole={GatewayRole.Operator}>
                <PartnerOutboundPage />
              </RequireRole>
            </RequireVerifiedTenant>
          }
        />
        <Route
          path="/accounts"
          element={
            <RequireVerifiedTenant>
              <AccountsPage />
            </RequireVerifiedTenant>
          }
        />
        <Route
          path="/purchase-orders"
          element={
            <RequireVerifiedTenant>
              <PurchaseOrdersPage />
            </RequireVerifiedTenant>
          }
        />
        <Route
          path="/expenses"
          element={
            <RequireVerifiedTenant>
              <ExpensesPaymentsPage />
            </RequireVerifiedTenant>
          }
        />
        <Route
          path="/history"
          element={
            <RequireVerifiedTenant>
              <SyncHistoryPage />
            </RequireVerifiedTenant>
          }
        />

        <Route
          path="/sync-rules"
          element={
            <RequireVerifiedTenant>
              <RequireRole minRole={GatewayRole.Admin}>
                <SyncRulesPage />
              </RequireRole>
            </RequireVerifiedTenant>
          }
        />
        <Route
          path="/email-intake"
          element={
            <RequireVerifiedTenant>
              <RequireRole minRole={GatewayRole.Admin}>
                <EmailIntakePage />
              </RequireRole>
            </RequireVerifiedTenant>
          }
        />
        <Route
          path="/payables-agent"
          element={
            <RequireVerifiedTenant>
              <RequireRole minRole={GatewayRole.Admin}>
                <PayablesAgentPage />
              </RequireRole>
            </RequireVerifiedTenant>
          }
        />
        <Route
          path="/ai-settings"
          element={
            <RequireVerifiedTenant>
              <RequireRole minRole={GatewayRole.Admin}>
                <AiSettingsPage />
              </RequireRole>
            </RequireVerifiedTenant>
          }
        />
        <Route
          path="/ai-learning"
          element={
            <RequireVerifiedTenant>
              <RequireRole minRole={GatewayRole.Admin}>
                <AiLearningPage />
              </RequireRole>
            </RequireVerifiedTenant>
          }
        />
        {/* Open to every signed-in role: the admin-only panels gate themselves, and everyone
            needs the change-password panel. */}
        <Route path="/settings" element={<SettingsPage />} />

        {/* The Setup Assistant is open to an UNVERIFIED tenant by design, mirroring the API's own
            exemption: the person whose registration or licence is not through yet is the one who
            needs setup help. The service answers them from the local catalogue only. */}
        <Route path="/assistant" element={<AssistantPage />} />

        {/* This company's standing with the iERP platform. Operators read; the actions gate
            themselves to administrators inside the page and the server refuses them regardless. */}
        <Route
          path="/platform"
          element={
            <RequireVerifiedTenant>
              <RequireRole minRole={GatewayRole.Operator}>
                <PlatformPage />
              </RequireRole>
            </RequireVerifiedTenant>
          }
        />

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
